import { Box, Button, Typography, Chip } from '@mui/material';
import { deleteAgreement } from '../../services/agreementsService';
import Swal from 'sweetalert2';
import { useState } from 'react';
import { useAuth } from '../../context/authContext';
import AgreementModal from './AgreementModal';
import {
  EditOutlined,
  DeleteOutlined,
  BusinessOutlined,
  DescriptionOutlined,
} from '@mui/icons-material';
import { ActionLoader } from '../loader';

const AgreementCard = ({ agreement, isAdmin, onUpdate }) => {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { user } = useAuth();

    const handleDelete = async () => {
        try {
            const confirmation = await Swal.fire({
                title: '¿Estás seguro de eliminar el convenio?',
                text: 'Esta acción no se puede deshacer',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: 'Sí, eliminar',
                cancelButtonText: 'Cancelar',
            });

            if (!confirmation.isConfirmed) return;
            setIsSubmitting(true);
            const response = await deleteAgreement(agreement.id_agreement, user);
            setIsSubmitting(false);
            if (response.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Convenio eliminado',
                    showConfirmButton: false,
                    timer: 1500,
                });
                onUpdate();
                if (onUpdate) onUpdate();
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error al eliminar el convenio',
                    timer: 1500,
                });
            }
        } catch (error) {
            console.error('Error deleting agreement:', error);
        }
    };

    const handleEdit = () => {
        setEditMode(true);
        setDrawerOpen(true);
    };

    const handleClose = () => {
        setDrawerOpen(false);
        setEditMode(false);
    };

    return (
        <>
            <Box
                sx={{
                    overflow: 'hidden',
                    boxShadow: 3,
                    border: 'none',
                    borderRadius: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                        boxShadow: 6,
                        transform: 'translateY(-4px)',
                    },
                    height: '100%',
                    background: 'linear-gradient(145deg, #ffffff, #f8f9fa)',
                    position: 'relative',
                    '::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        background: 'linear-gradient(90deg, #1976d2, #4caf50)',
                    }
                }}
                onClick={() => {
                    setEditMode(false);
                    setDrawerOpen(true);
                }}
            >
                {/* Badge de estado */}
                {agreement.status && (
                    <Chip
                        label={agreement.status}
                        size="small"
                        sx={{
                            position: 'absolute',
                            top: 16,
                            right: 16,
                            zIndex: 1,
                            fontWeight: 'bold',
                            backgroundColor: agreement.status === 'Activo' ? 'success.light' : 'error.light',
                            color: 'white'
                        }}
                    />
                )}

                {/* Imagen con overlay */}
                <Box
                    sx={{
                        position: 'relative',
                        height: '220px',
                        width: '100%',
                        overflow: 'hidden',
                        '&:hover img': {
                            transform: 'scale(1.05)',
                        }
                    }}
                >
                    <img
                        src={agreement.image}
                        alt={agreement.title}
                        style={{
                            objectFit: 'cover',
                            width: '100%',
                            height: '100%',
                            transition: 'transform 0.5s ease',
                        }}
                        loading='lazy'
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: '30%',
                            background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
                            display: 'flex',
                            alignItems: 'flex-end',
                            p: 2
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                color: 'white',
                                fontWeight: 'bold',
                                textShadow: '1px 1px 3px rgba(0,0,0,0.5)'
                            }}
                        >
                            {agreement.title}
                        </Typography>
                    </Box>
                </Box>

                {/* Contenido */}
                <Box sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    {/* Info con iconos */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                        <BusinessOutlined fontSize="small" color="primary" />
                        <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                            {agreement.business_name}
                        </Typography>
                    </Box>

                    <Box sx={{ mt: 1, mb: 2 }}>
                        <Typography 
                            variant="body2" 
                            sx={{
                                color: 'text.secondary',
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                flexGrow: 1,
                            }}
                        >
                            <DescriptionOutlined 
                                fontSize="small" 
                                color="primary" 
                                sx={{ verticalAlign: 'middle', mr: 1 }} 
                            />
                            {agreement.description}
                        </Typography>
                    </Box>
                </Box>

                {/* Footer con botón de eliminar y editar */}
                {isAdmin && (
                    <Box 
                        sx={{ 
                            p: 2, 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            gap: 2,
                            background: 'rgba(0, 0, 0, 0.02)',
                            borderTop: '1px solid rgba(0, 0, 0, 0.08)',
                        }}
                    >
                        <Button
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteOutlined />}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDelete();
                            }}
                            sx={{
                                borderColor: '#C42251',
                                color: '#C42251',
                                textTransform: 'none',
                                minWidth: 140,
                                minHeight: '36px',
                                padding: '6px 16px',
                                lineHeight: '1.5',
                                '&:hover': {
                                    borderColor: '#A01D45',
                                    backgroundColor: 'rgba(196, 34, 81, 0.04)',
                                },
                            }}
                        >
                            Eliminar
                        </Button>

                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<EditOutlined />}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleEdit();
                            }}
                            sx={{
                                background: 'linear-gradient(to right, #09274A, #0488AF)',
                                color: '#fff',
                                textTransform: 'none',
                                minWidth: 140,
                                minHeight: '36px',
                                padding: '6px 16px',
                                lineHeight: '1.5',
                                '&:hover': {
                                    background: 'linear-gradient(to right, #071f3d, #03779C)',
                                },
                            }}
                        >
                            Editar
                        </Button>
                    </Box>
                )}
            </Box>
            <ActionLoader open={isSubmitting} onClose={() => {}}></ActionLoader>
            {/* Renderizar el Modal */}
            <AgreementModal
                agreement={agreement}
                open={drawerOpen}
                onClose={handleClose}
                mode={editMode ? 'edit' : 'view'}
                onUpdate={onUpdate}
                setIsSubmitting={setIsSubmitting}
            />
        </>
    );
};

export default AgreementCard;